import { useCallback, useRef, useState } from 'react';
import { hasMediaDevices, insecureMediaMessage } from '../../../utils/mediaSupport';

/**
 * Candidate camera capture for the meeting room. The camera is REQUIRED to join, so `start()`
 * surfaces a clear error the caller can gate on. `setEnabled(false)` keeps the stream alive but
 * pauses the video track (so the "Camera blocked" tile shows without re-prompting for permission).
 *
 *   const cam = useCamera();
 *   await cam.start();            // throws / returns null if denied
 *   cam.setEnabled(false);        // pause (mute) the camera
 *   cam.stop();                   // release the device
 */
export default function useCamera() {
  const streamRef = useRef(null);
  const generationRef = useRef(0);
  const pendingStartRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [enabled, setEnabledState] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const start = useCallback(async ({ deviceId } = {}) => {
    setError('');
    if (!hasMediaDevices()) {
      const msg = insecureMediaMessage();   // http LAN IP etc. → navigator.mediaDevices is undefined
      setError(msg);
      throw new Error(msg);
    }
    let startGeneration = null;
    try {
      const current = streamRef.current;
      if (current?.getVideoTracks().some((track) => track.readyState === 'live')) {
        current.getVideoTracks().forEach((track) => { track.enabled = true; });
        setStream(current);
        setEnabledState(true);
        return current;
      }
      if (pendingStartRef.current) return pendingStartRef.current;
      try { current?.getTracks().forEach((track) => track.stop()); } catch { /* ignore */ }
      streamRef.current = null;
      setStream(null);
      startGeneration = ++generationRef.current;
      setStarting(true);
      const pending = navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      }).then((s) => {
        if (startGeneration !== generationRef.current) {
          s.getTracks().forEach((track) => track.stop());
          return null;
        }
        streamRef.current = s;
        setStream(s);
        setEnabledState(true);
        const videoTrack = s.getVideoTracks()[0];
        videoTrack?.addEventListener('ended', () => {
          if (streamRef.current !== s) return;
          streamRef.current = null;
          setStream(null);
          setEnabledState(false);
        }, { once: true });
        return s;
      });
      pendingStartRef.current = pending;
      try { return await pending; }
      finally {
        if (pendingStartRef.current === pending) {
          pendingStartRef.current = null;
          setStarting(false);
        }
      }
    } catch (e) {
      if (startGeneration !== null && startGeneration !== generationRef.current) return null;
      const reason = e && e.name === 'NotAllowedError'
        ? 'Camera permission was denied. Allow the camera to join the interview.'
        : (e && e.message) || 'Could not access the camera.';
      setError(reason);
      throw e;
    }
  }, []);

  const setEnabled = useCallback((on) => {
    const s = streamRef.current;
    if (s) s.getVideoTracks().forEach((t) => { t.enabled = on; });
    setEnabledState(on);
  }, []);

  const stop = useCallback(() => {
    generationRef.current += 1;
    pendingStartRef.current = null;
    setStarting(false);
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
    setStream(null);
    setEnabledState(false);
  }, []);

  return { stream, enabled, starting, error, start, stop, setEnabled };
}
